import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { REACTION_EMOJI, type MessageView, type UserView } from '@olmt/shared'
import {
  canCopyMessage,
  canDeleteMessage,
  canEditMessage,
  canForwardMessage,
  canPinMessage,
  canReactTo,
  canSelectMessage,
  messagePreview,
  myReaction,
} from '../lib/chat'
import { mentionSegments, mentionsMe, type MentionSegment } from '../lib/mentions'
import { firstStrongDirection } from '../lib/bidi'
import { formatTime } from '../lib/time'
import { useT } from '../lib/i18n'
import { MessageMedia } from './MessageMedia'
import { PollBody } from './PollBody'
import { CheckIcon, DoubleCheckIcon, ReplyIcon, PhoneIcon, VideoIcon } from './Icons'
import { Spinner } from './Spinner'

type MessageBubbleProps = {
  message: MessageView
  own: boolean
  /** Group chats label each author; DMs never do. */
  showAuthor: boolean
  /** Directly follows another message from the same author. */
  grouped: boolean
  replyTo: MessageView | null
  meId: string | null
  /**
   * The chat's members (W10): a body's `@handle` is a mention only when it names one of them — a
   * body never parses itself, and text that merely looks like a handle stays as it was typed.
   */
  members: UserView[]
  /**
   * The peer has read this message (A15): the cursor comes from `ChatView.reads` seeded at load
   * time and from live `read` frames, so a reload does not reset it to "unread".
   */
  read: boolean
  /**
   * This row *is* the chat's one pinned message (W5): its menu offers Unpin, not Pin. The answer
   * comes from `isPinnedMessage` — nothing here compares ids itself.
   */
  pinned: boolean
  /**
   * The message a search jump just landed on (W3): the bubble wears a brass ring for a few seconds,
   * so the reader sees *where* the hit they chose actually is. The pane owns the timing; this only
   * paints the state.
   */
  highlighted: boolean
  /**
   * The thread is in selection mode (W4): a tap toggles this bubble instead of opening its menu,
   * and the menu, the long-press and the reply affordance are all suppressed.
   */
  selecting: boolean
  /** This bubble is one of the selected ones; it wears a ring and a check in its meta row. */
  selected: boolean
  onReply: (message: MessageView) => void
  onEdit: (message: MessageView) => void
  onDelete: (message: MessageView) => void
  onToggleSelect: (message: MessageView) => void
  onCopy: (message: MessageView) => void
  onForward: (message: MessageView) => void
  /** Turn selection mode on, with this bubble as its first pick. */
  onStartSelect: (message: MessageView) => void
  /** Pin this message (W5), or take the chat's pin down when `pinned` is false. */
  onSetPinned: (message: MessageView, pinned: boolean) => void
  /**
   * React with this emoji (W9) — from a chip under the bubble or from the strip in its menu. The
   * toggle rule lives with the caller: the emoji that is already mine clears instead (the bubble
   * only says which chip is mine, through `myReaction`).
   */
  onReact: (message: MessageView, emoji: string) => void
  /**
   * Answer one option of the poll this message is (W11). The api owns the toggle — my own answer is
   * taken back, a single-choice poll replaces it, a multiple-choice one adds to it — so the caller
   * only sends the row's index and says the failure out loud.
   */
  onVote: (message: MessageView, optionIndex: number) => void
}

/**
 * A body's runs (W10). The `@handle` runs that name a member of the chat are brass and wrapped in
 * `<bdi>`; everything else is the body's own text, unchanged, so text that only looks like a handle
 * is left exactly as it was typed.
 *
 * The isolate is the point of the `<bdi>`, not decoration: a handle is Latin even inside an Arabic
 * body, and without it the bidi algorithm is free to move `@nadia`'s pieces around the right-to-left
 * runs beside it (§7.9 — a handle stays one unit). It also keeps the paragraph's own `dir="auto"`
 * honest: an isolated run is not part of the text that decides the paragraph's direction.
 */
function mentionRuns(segments: MentionSegment[]): React.ReactNode {
  return segments.map((segment, index) =>
    segment.member === null ? (
      segment.text
    ) : (
      <bdi key={index} className="font-medium text-accent">
        {segment.text}
      </bdi>
    ),
  )
}

/** How a finger opens the bubble's actions: held still for 450ms, or the pointer has moved on. */
const LONG_PRESS_MS = 450
const LONG_PRESS_SLOP_PX = 10

type BubbleAction = {
  key: string
  label: string
  danger?: boolean
  /**
   * W9: the item leaves the panel open when chosen. React turns the panel into its emoji strip
   * instead of closing, because the strip is what the item asked for and it belongs to the same
   * anchored panel — so the same Escape still hands focus back to the bubble.
   */
  keepsOpen?: boolean
  onSelect: () => void
}

type BubbleMenuProps = {
  /** Where the finger or the cursor was, in viewport coordinates. */
  at: { x: number; y: number }
  /** The bubble that opened it; Escape hands the focus back to it. */
  opener: HTMLElement | null
  actions: BubbleAction[]
  onClose: () => void
  /** W9: the six-emoji strip is open above the items; React's own item turned it on. */
  picking: boolean
  /** W9: the emoji this member already used, or null. The strip marks it and the pick clears it. */
  mine: string | null
  onPickEmoji: (emoji: string) => void
}

/**
 * A bubble's actions (W2), opened by long-press or the context menu and anchored where that
 * happened. It lives in a fixed layer so the thread's scroller cannot clip it; it is painted
 * hidden until measured, so it can never flash at the wrong corner and jump. Focus moves to the
 * first item on open and Escape returns it to the bubble — the design's rule for every panel
 * (docs/DESIGN.md §6.5).
 *
 * W9 adds the emoji strip: the React item opens it inside this same panel, above the items, and
 * focus moves into it — the strip's six buttons are menu items too, so Tab and Enter reach them.
 * Escape keeps its one meaning (close, focus back to the bubble), which is why the strip lives
 * here rather than in a second floating panel.
 */
function BubbleMenu({ at, opener, actions, onClose, picking, mine, onPickEmoji }: BubbleMenuProps) {
  const t = useT()
  const panelRef = useRef<HTMLDivElement>(null)
  const [placed, setPlaced] = useState<{ left: number; top: number } | null>(null)

  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!panel) return
    const size = panel.getBoundingClientRect()
    const margin = 8
    const gap = 6
    const left =
      at.x + gap + size.width > window.innerWidth - margin
        ? Math.max(margin, at.x - gap - size.width)
        : at.x + gap
    const top =
      at.y + gap + size.height > window.innerHeight - margin
        ? Math.max(margin, at.y - gap - size.height)
        : at.y + gap
    setPlaced({ left, top })
    // Focus lands in the panel: the first item, or the first emoji while the strip is up — the
    // strip is drawn above the items (W9), and `preventScroll` matters because the panel lives
    // over the thread's scroller, where a focus scroll would drag the message away.
    const first = picking ? '[data-react-emoji]' : '[role="menuitem"]'
    panel.querySelector<HTMLElement>(first)?.focus({ preventScroll: true })
    // The strip's own width change is a re-measure: the panel must stay inside the viewport.
  }, [at, picking])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      opener?.focus({ preventScroll: true })
      onClose()
    }
    // The panel is pinned to a viewport point; a scroll would leave it pointing at nothing.
    const onScroll = () => onClose()
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [onClose, opener])

  return (
    <div
      className="fixed inset-0 z-30"
      role="presentation"
      // `pointerdown`, not `click`: the click that follows a long press lands wherever the finger
      // lifted — often on this layer — and would close the menu the press just opened.
      onPointerDown={onClose}
      // Android's own long-press menu must not land on top of ours.
      onContextMenu={(event) => event.preventDefault()}
    >
      <div
        ref={panelRef}
        role="menu"
        aria-label={t('chat.messageActions')}
        style={{ left: placed?.left, top: placed?.top, visibility: placed ? 'visible' : 'hidden' }}
        className={`fixed overflow-hidden rounded-panel bg-surface py-1 shadow-lift ring-1 ring-line-strong ${
          // Six 44px targets need the room; the panel is the only thing that grows.
          picking ? 'w-72' : 'w-44'
        }`}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {picking && (
          /* The strip is the app's own lit state, one emoji per button: the brass fill, not only a
             brass hue, marks the emoji this member already used — so choosing it reads as taking
             the reaction back, and colour vision is not the only channel. The glyph is hidden from
             the name because `chat.reactWith` / `chat.removeReaction` already speak it. */
          <div
            role="group"
            aria-label={t('chat.react')}
            className="flex items-center justify-between gap-0.5 border-b border-line px-1.5 pb-1 pt-0.5"
          >
            {REACTION_EMOJI.map((emoji) => {
              const chosen = mine === emoji
              return (
                <button
                  key={emoji}
                  type="button"
                  role="menuitem"
                  data-react-emoji
                  aria-label={t(chosen ? 'chat.removeReaction' : 'chat.reactWith', { emoji })}
                  onClick={() => {
                    onClose()
                    onPickEmoji(emoji)
                  }}
                  className={`press flex size-11 items-center justify-center rounded-full text-xl ${
                    chosen ? 'bg-accent text-on-accent' : 'text-ink hover:bg-surface2'
                  }`}
                >
                  <span aria-hidden="true">{emoji}</span>
                </button>
              )
            })}
          </div>
        )}
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            role="menuitem"
            onClick={() => {
              if (!action.keepsOpen) onClose()
              action.onSelect()
            }}
            className={`flex min-h-11 w-full items-center px-3.5 text-start text-ui transition-colors duration-quick hover:bg-surface2 ${
              action.danger ? 'text-danger' : 'text-ink'
            }`}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Who is speaking is carried by **position** first — own messages sit on the right, everyone
 * else's on the left — and only then by hue (plum vs umber). Position survives every form of colour
 * vision deficiency and both scripts; the hue is the second cue, not the only one. The two bubble
 * fills are 1.44:1 apart in luminance, so they are deliberately *not* expected to separate on
 * lightness alone (docs/DESIGN.md §Colour).
 *
 * The body carries `dir="auto"`: an Arabic paragraph is then laid out right-to-left inside the
 * bubble while the bubble itself stays on the side the layout put it — see `.message-text`.
 *
 * A bubble is also the message's action surface (W2): long-press on touch, `contextmenu` with a
 * pointer, and Enter/Space on the focused bubble all open the same menu. Edit and Delete are
 * offered only when the pure rules in `lib/chat.ts` say so — nothing here re-derives ownership.
 *
 * W4 extends the menu with Copy, Forward and Select from the same set of rules, and gives the
 * bubble a second mode: while the thread is selecting, the menu is suppressed and a tap toggles
 * this message into the selection — the media controls and links inside the bubble are stopped in
 * the capture phase so a photo tap cannot both open its viewer and toggle.
 *
 * W5 adds the pin: one item, Pin or Unpin, whose door is `canPinMessage` and whose word is the
 * `pinned` prop the thread derived from `isPinnedMessage` — so the menu never disagrees with the
 * bar above the thread about which message the chat is holding.
 *
 * W9 adds reactions: a row of chips under the bubble — one per emoji anyone used, with its count —
 * and a React item whose strip holds the six emoji. Both read `canReactTo` and `myReaction`, and
 * both go through one `onReact` door, so "tap mine to take it back, tap another to switch" is one
 * decision, made where the stored state lives. The row sits *outside* the bubble, so the body's
 * `dir="auto"` and the meta row are not in its line of fire; the reply affordance is hidden while
 * the thread is selecting, and so is the row, for the same reason.
 *
 * W11 gives a `poll` message its answer rows under the question (`PollBody`), inside the bubble:
 * the body is the question and is drawn exactly as any other body, and the rows are the message's
 * control. The block follows the same selection rule as the media subtree — `inert` while the
 * thread is selecting — so a tap that belongs to the selection cannot cast a vote on the way.
 */
export function MessageBubble({
  message,
  own,
  showAuthor,
  grouped,
  replyTo,
  meId,
  members,
  read,
  pinned,
  highlighted,
  selecting,
  selected,
  onReply,
  onEdit,
  onDelete,
  onToggleSelect,
  onCopy,
  onForward,
  onStartSelect,
  onSetPinned,
  onReact,
  onVote,
}: MessageBubbleProps) {
  const t = useT()
  const bubbleRef = useRef<HTMLDivElement>(null)
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null)
  /** W9: the menu is showing its emoji strip instead of just the React item. */
  const [reactPicker, setReactPicker] = useState(false)
  const press = useRef<{ x: number; y: number; pointerId: number } | null>(null)
  const pressTimer = useRef<number | null>(null)

  const clearPress = () => {
    if (pressTimer.current !== null) window.clearTimeout(pressTimer.current)
    pressTimer.current = null
    press.current = null
  }

  // A bubble that leaves the list mid-press (a re-render replaced it, the thread changed) must not
  // leave a timer behind to open a menu on a row nobody is looking at.
  useEffect(() => clearPress, [])

  if (message.kind === 'system') {
    return (
      <li className="my-3 flex justify-center">
        <span className="rounded-full bg-surface2/70 px-3 py-1 text-meta text-muted ring-1 ring-line ring-inset">
          {message.body ?? t('chat.systemMessage')}
        </span>
      </li>
    )
  }

  /** Opens the menu at a point; from the keyboard the point is the bubble itself. */
  const openMenu = (x: number | null, y: number | null) => {
    const rect = bubbleRef.current?.getBoundingClientRect()
    setReactPicker(false)
    setMenuAt(
      x !== null && y !== null
        ? { x, y }
        : { x: rect ? rect.left + rect.width : 0, y: rect ? rect.top : 0 },
    )
  }

  /** Every way out of the menu: a choice, a press outside, a scroll, Escape. */
  const closeMenu = () => {
    setMenuAt(null)
    setReactPicker(false)
  }

  const onBubbleContextMenu = (event: React.MouseEvent) => {
    // Our own menu replaces the browser's; the coordinates are where the pointer was.
    event.preventDefault()
    clearPress()
    // While the thread is selecting there is no menu to open — the swipe-free way to add a bubble
    // to the selection is to tap it, and a right-click must not jump out of the mode.
    if (selecting) return
    openMenu(event.clientX, event.clientY)
  }

  const onBubbleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Keys belong to the focused bubble, not to a control inside it (the media button, a link):
    // Enter there is that control's own activation.
    if (event.target !== event.currentTarget) return
    const activates = event.key === 'Enter' || event.key === ' '
    const opensMenu = event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')
    if (!activates && !opensMenu) return
    event.preventDefault()
    clearPress()
    if (selecting) {
      // The bubble's one action while selecting is the toggle; the menu key has no menu to reach.
      if (activates) onToggleSelect(message)
      return
    }
    openMenu(null, null)
  }

  /**
   * A tap in selection mode belongs to the selection. This runs in the capture phase, before the
   * media controls' own click handlers, and stops the event there: a photo must not open its viewer
   * *and* toggle, and a video must not start playing on the way.
   */
  const onBubbleClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onToggleSelect(message)
  }

  const onBubblePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // The mouse's door is the context menu; a finger or a pen holds still for 450ms. While
    // selecting, a press is the tap that toggles — there is no long-press menu to wait for.
    if (selecting || event.pointerType === 'mouse' || !event.isPrimary || event.button !== 0) return
    const { clientX, clientY, pointerId } = event
    clearPress()
    press.current = { x: clientX, y: clientY, pointerId }
    pressTimer.current = window.setTimeout(() => {
      clearPress()
      openMenu(clientX, clientY)
    }, LONG_PRESS_MS)
  }

  const onBubblePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = press.current
    if (!start || start.pointerId !== event.pointerId) return
    // A finger that travels is scrolling or selecting, not holding.
    if (
      Math.abs(event.clientX - start.x) > LONG_PRESS_SLOP_PX ||
      Math.abs(event.clientY - start.y) > LONG_PRESS_SLOP_PX
    ) {
      clearPress()
    }
  }

  const actions: BubbleAction[] = []
  // W9's item sits first: it is the lightest action and it opens the strip inside this same panel,
  // so a keyboard that opened the menu already holds the door to the six emoji.
  if (canReactTo(message)) {
    actions.push({
      key: 'react',
      label: t('chat.react'),
      keepsOpen: true,
      onSelect: () => setReactPicker(true),
    })
  }
  actions.push({ key: 'reply', label: t('chat.reply'), onSelect: () => onReply(message) })
  // W4's three items sit between Reply and the write doors: Copy and Forward are the two a reader
  // reaches for most often, and Select is the door into the thread's selection mode. Edit and
  // Delete keep their places, and their rules.
  if (canCopyMessage(message)) {
    actions.push({ key: 'copy', label: t('chat.copy'), onSelect: () => onCopy(message) })
  }
  if (canForwardMessage(message)) {
    actions.push({ key: 'forward', label: t('chat.forward'), onSelect: () => onForward(message) })
  }
  if (canSelectMessage(message)) {
    actions.push({ key: 'select', label: t('chat.select'), onSelect: () => onStartSelect(message) })
  }
  // W5's one item, or its inverse: Pin on any row that is not a tombstone, Unpin on the chat's
  // current pinned message. The rule is `canPinMessage`, and which word the item wears is the
  // chat's own state — both arrive from outside this component.
  if (canPinMessage(message)) {
    actions.push({
      key: 'pin',
      label: t(pinned ? 'chat.unpin' : 'chat.pin'),
      onSelect: () => onSetPinned(message, !pinned),
    })
  }
  if (canEditMessage(message, meId)) {
    actions.push({ key: 'edit', label: t('chat.edit'), onSelect: () => onEdit(message) })
  }
  if (canDeleteMessage(message, meId)) {
    actions.push({ key: 'delete', label: t('chat.delete'), danger: true, onSelect: () => onDelete(message) })
  }

  const authorName = message.author?.displayName ?? t('chat.unknownAuthor')
  const deleted = message.deletedAt !== null
  /** W9: the emoji this member used, or null — one answer the chips and the strip both read. */
  const mine = myReaction(message, meId)
  /**
   * W10 — the body's runs of plain text and member names: one scan for both places a body is
   * spelled (the paragraph, and a file's caption), keyed on the body so a thread re-render does not
   * re-read every bubble's words.
   */
  const bodySegments = useMemo(
    () => (message.body === null ? [] : mentionSegments(message.body, members)),
    [message.body, members],
  )

  return (
    <li
      // The jump's target (W3): the thread finds the row by this id and scrolls it into view. The
      // id is the message's, which is what the highlight in the store names.
      data-message-id={message.id}
      className={`message-row group flex items-end gap-1 ${own ? 'flex-row-reverse' : 'flex-row'} ${
        grouped ? 'mt-0.5' : 'mt-2.5'
      }`}
    >
      <div className="min-w-0 max-w-[86%] sm:max-w-[72%]">
        {showAuthor && !own && (
          <p className="mb-1 flex items-center gap-1.5 ps-1 text-meta font-medium text-muted">
            <span aria-hidden="true" className="size-1.5 rounded-full bg-accent" />
            <bdi>{authorName}</bdi>
          </p>
        )}
        <div
          ref={bubbleRef}
          // The bubble is the message's action surface: focusable, with a popup. `group` is what
          // lets `aria-haspopup`/`aria-expanded` sit on it *and* keeps the message's own text and
          // media readable — a widget role like `button` would flatten them for screen readers and
          // nest the media control inside a control. While the thread is selecting there is no
          // popup to announce, so its attributes go away with the menu.
          role="group"
          tabIndex={0}
          aria-haspopup={selecting ? undefined : 'menu'}
          aria-expanded={selecting ? undefined : menuAt !== null}
          onContextMenu={onBubbleContextMenu}
          onClickCapture={selecting ? onBubbleClickCapture : undefined}
          onKeyDown={onBubbleKeyDown}
          onPointerDown={onBubblePointerDown}
          onPointerMove={onBubblePointerMove}
          onPointerUp={clearPress}
          onPointerCancel={clearPress}
          className={`rounded-bubble px-3.5 py-2.5 text-body ${selecting ? 'cursor-pointer' : ''} ${
            own ? 'bg-bubble-out text-bubble-out-ink' : 'bg-bubble-in text-bubble-in-ink'
          } ${own ? (grouped ? 'rounded-tr-anchor' : '') : grouped ? 'rounded-tl-anchor' : ''} ${
            // A jump's landing (W3) replaces the bubble's own hairline rather than piling a second
            // ring on it: one brass ring, drawn outside the bubble so it reads at a glance. A
            // selected bubble (W4) wears that same ring — the bar above the composer is what says
            // whether the thread is selecting, and the check in the meta row marks each pick.
            selected || highlighted ? 'ring-2 ring-accent' : own ? '' : 'ring-1 ring-line ring-inset'
          }`}
        >
          {message.forwarded && !deleted && (
            /* The mark a copy carries and its original never does (W4). It is a block of its own at
               the top of the bubble, like the edited tag at the bottom: it says nothing about the
               words below it, so it must not take their direction — the body keeps its own
               `dir="auto"` and an Arabic one still starts at its own edge. */
            <p className={`mb-1 text-meta ${own ? 'text-bubble-out-meta' : 'text-muted'}`}>
              <bdi>{t('chat.forwarded')}</bdi>
            </p>
          )}

          {replyTo && (
            /* The quoted block takes the direction of the text it quotes. Without this the panel
               keeps the layout's LTR: an Arabic excerpt sits flush right while its author name and
               the brass rule stay on the left, so the two halves of one quote end up at opposite
               ends of a full-width panel with a gap between them (measured on the shipped build —
               see docs/design/). The quote is not chrome around a message; it *is* the message it
               quotes, and RTL apps align it as such. `dir` here also flips `border-s-2`.
               A quoted excerpt stays unlit (W10): it is a *preview* — the same string the chat list
               and search render without a member list — and what lights up is the body below. */
            <div
              dir={firstStrongDirection(messagePreview(replyTo, meId)) ?? undefined}
              className={`mb-2 overflow-hidden rounded-md bg-black/20 ${
                own ? 'ring-bubble-out-meta/25' : 'ring-black/30'
              } ring-1 ring-inset`}
            >
              <div className={`border-s-2 py-1.5 ps-2.5 pe-2 ${own ? 'border-bubble-out-meta/50' : 'border-accent/70'}`}>
                <span className="block text-meta font-semibold">
                  {replyTo.authorId === meId ? t('chat.you') : (replyTo.author?.displayName ?? t('chat.unknownAuthor'))}
                </span>
                {/* A quoted Arabic line is its own paragraph too — same rule as the body below. */}
                <p dir="auto" className="message-text line-clamp-2 text-meta opacity-80">
                  {messagePreview(replyTo, meId)}
                </p>
              </div>
            </div>
          )}

          {deleted ? (
            <p className="text-meta italic opacity-70">{t('chat.messageDeleted')}</p>
          ) : (
            <>
              {message.kind === 'call' ? (
                <p className="flex items-center gap-2.5 py-0.5">
                  <span
                    className={`flex size-8 items-center justify-center rounded-full ${
                      own ? 'bg-black/25' : 'bg-accent/15 text-accent'
                    }`}
                  >
                    {message.body === 'video' ? <VideoIcon size={16} /> : <PhoneIcon size={16} />}
                  </span>
                  <span className="font-medium">{message.body === 'video' ? t('chat.videoCall') : t('chat.voiceCall')}</span>
                </p>
              ) : message.media ? (
                /* While the thread is selecting, the media is not a control: a tap is the
                   selection's. The photo viewer and the video player are already stopped by the
                   bubble's capture handler, but a voice note's track acts on pointer-down rather
                   than on click, so the whole subtree is made inert instead — keyboard included —
                   and the bubble behind it is what receives the tap. */
                <div inert={selecting || undefined}>
                  <MessageMedia media={message.media} own={own} fallbackName={message.body} />
                </div>
              ) : null}
              {message.body && message.media === null && (
                <p dir="auto" className="message-text">
                  {mentionRuns(bodySegments)}
                </p>
              )}
              {message.body && message.media !== null && message.media.kind === 'file' && (
                /* A file's caption is the message's own words as well, so a name in it is a name:
                   the same runs as the paragraph above, only smaller and dimmed by the meta tone. */
                <p dir="auto" className="message-text mt-1 text-meta opacity-70">
                  {mentionRuns(bodySegments)}
                </p>
              )}
              {message.kind === 'poll' && (
                /* W11 — the options under the question the body just drew. While the thread is
                   selecting, the whole block goes `inert`: a tap on a bubble is the selection's
                   then, and a vote must not slip through — the same rule the media subtree above
                   follows. */
                <div inert={selecting || undefined}>
                  <PollBody message={message} meId={meId} own={own} onVote={onVote} />
                </div>
              )}
            </>
          )}

          <div
            className={`mt-1 flex items-center justify-end gap-1.5 text-meta ${
              own ? 'text-bubble-out-meta' : 'text-muted'
            }`}
          >
            {/* W10: a word, not only a colour — brass alone says nothing to a reader who cannot see
                it. Only on somebody else's message: the api never notifies an author of their own
                mention, so "you were named" on your own bubble would be a sentence about nobody. */}
            {!own && !deleted && mentionsMe(message, meId) && (
              <span className="font-semibold text-accent">{t('chat.mentionedYou')}</span>
            )}
            {/* The picked state as a shape, not only as the ring's hue (W4): the ring can be read
                as W3's jump, so a selected bubble carries a check as well. */}
            {selected && (
              <span aria-hidden="true" className="flex size-4 items-center justify-center rounded-full bg-accent text-on-accent">
                <CheckIcon size={12} />
              </span>
            )}
            {message.editedAt !== null && !deleted && <span>{t('chat.edited')}</span>}
            <time className="numeric" dateTime={new Date(message.createdAt).toISOString()}>
              {formatTime(message.createdAt)}
            </time>
            {/* The receipt, on your own bubbles only: one check once it is stored, a second (and
                the accent) once the other side's cursor has passed it. The shape carries the state,
                not the hue — brass on the plum bubble is 5.9:1 either way. The native client draws
                the same pair (`DeliveryMark`). */}
            {own && !deleted && (
              <span className={`flex items-center ${read ? 'text-accent' : ''}`}>
                {message.pending ? (
                  <Spinner size={11} label={t('chat.sending')} />
                ) : (
                  <>
                    {read ? <DoubleCheckIcon size={15} /> : <CheckIcon size={15} />}
                    <span className="sr-only">{t(read ? 'chat.read' : 'chat.sent')}</span>
                  </>
                )}
              </span>
            )}
          </div>
        </div>

        {!selecting && canReactTo(message) && message.reactions.length > 0 && (
          /* W9: the chips sit *under* the bubble and outside it, so the body's `dir="auto"` and the
             meta row above are untouched — the row is a second block, not a line of the message.
             It is placed in the bubble's own alignment: `justify-end` for yours, `justify-start`
             for theirs, which follows the layout's direction rather than the screen's. One chip
             per entry the api sent (it already sorts them in the set's order). Mine wears the lit
             state — a solid brass fill against everyone else's hairline well — so "mine" is carried
             by shape as well as hue, and by the brass the app uses for "yours to act on". The name
             is the action plus the count (`Remove 👍 2`), so the chip's visible number is never
             silent to a screen reader. */
          <div className={`mt-1 flex flex-wrap items-center gap-1 ${own ? 'justify-end' : 'justify-start'}`}>
            {message.reactions.map((reaction) => {
              const isMine = reaction.emoji === mine
              return (
                <button
                  key={reaction.emoji}
                  type="button"
                  onClick={() => onReact(message, reaction.emoji)}
                  className={`press flex min-h-11 items-center gap-1.5 rounded-full px-2.5 text-sm ${
                    isMine
                      ? 'bg-accent font-semibold text-on-accent hover:bg-accent-strong'
                      : 'bg-surface2 text-ink ring-1 ring-line-strong ring-inset hover:bg-surface3'
                  }`}
                >
                  <span className="sr-only">
                    {t(isMine ? 'chat.removeReaction' : 'chat.reactWith', { emoji: reaction.emoji })}
                  </span>
                  <span aria-hidden="true" className="leading-none">
                    {reaction.emoji}
                  </span>
                  <span className="numeric">{reaction.userIds.length}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {!deleted && !selecting && (
        <button
          type="button"
          aria-label={t('chat.replyTo', { target: own ? t('chat.yourMessage') : authorName })}
          title={t('chat.reply')}
          onClick={() => onReply(message)}
          className="reply-affordance mb-1 flex size-11 shrink-0 items-center justify-center rounded-full text-muted transition hover:bg-surface2 hover:text-accent focus-visible:opacity-100"
        >
          <ReplyIcon size={16} />
        </button>
      )}

      {menuAt && (
        <BubbleMenu
          at={menuAt}
          opener={bubbleRef.current}
          actions={actions}
          picking={reactPicker}
          mine={mine}
          onPickEmoji={(emoji) => onReact(message, emoji)}
          onClose={closeMenu}
        />
      )}
    </li>
  )
}
